<div #modalHeader></div>
<div [ngSwitch]="currentStage" class="flex justify-center mt-6">
  <ng-container *ngSwitchCase="'first_owner'">
    <ng-template [ngTemplateOutlet]="firstOwnerTemplate"></ng-template>
  </ng-container>

  <ng-container *ngSwitchCase="'properties_match'">
    <ng-template [ngTemplateOutlet]="propertiesTemplate"></ng-template>
  </ng-container>

  <ng-container *ngSwitchCase="'commission'">
    <ng-template [ngTemplateOutlet]="commissionTemplate"></ng-template>
  </ng-container>

  <ng-container *ngSwitchCase="'summary'">
    <ng-template [ngTemplateOutlet]="summaryTemplate"></ng-template>
  </ng-container>
</div>

<ng-template #firstOwnerTemplate>
  <div class="max-w-[640px]">
    <h1>Set up an owner</h1>
    <p>
      This is the person who will receive the statements you send. We automatically generate statements for you, and you
      can choose to manually share those with your owners when you're ready.
    </p>
    <ng-container *ngIf="ownersState$ | async as owners">
      <ng-container *ngIf="!owners.loading; else loading">
        <ng-container *ngIf="owners.data?.length; else newOwner">
          <mat-accordion displayMode="flat" *ngIf="ownerStep$ | async as step">
            <mat-expansion-panel
              [disabled]="step === StepEnum.Existing"
              [expanded]="step === StepEnum.Existing"
              (opened)="setOwnerStep(StepEnum.Existing)"
              hideToggle>
              <mat-expansion-panel-header>
                <mat-panel-title [ngClass]="{ link: step !== StepEnum.Existing }">
                  <div class="flex items-center gap-3">
                    <mat-icon>menu</mat-icon>
                    <h3>Select an existing owner</h3>
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>

              <mat-radio-group [(ngModel)]="owner" (ngModelChange)="owner = $event" class="flex flex-col">
                <mat-radio-button *ngFor="let owner of owners.data" [value]="owner">
                  <div class="flex items-center">{{ owner.firstName }} {{ owner.name }}</div>
                </mat-radio-button>
              </mat-radio-group>
            </mat-expansion-panel>

            <mat-expansion-panel
              [disabled]="step === StepEnum.New"
              [expanded]="step === StepEnum.New"
              (opened)="setOwnerStep(StepEnum.New)"
              hideToggle>
              <mat-expansion-panel-header>
                <mat-panel-title [ngClass]="{ link: step !== StepEnum.New }">
                  <div class="flex items-center gap-3">
                    <mat-icon>add_circle</mat-icon>
                    <h3>Create new owner</h3>
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>
              <hospitable-statements-owner-form [(owner)]="owner"></hospitable-statements-owner-form>
            </mat-expansion-panel>
          </mat-accordion>
        </ng-container>
      </ng-container>
    </ng-container>
    <ng-template #newOwner>
      <hospitable-statements-owner-form [(owner)]="owner"></hospitable-statements-owner-form>
    </ng-template>

    <div class="flex justify-end mt-6">
      <sbnb-button
        [loading]="creatingOwner"
        [disabled]="!owner.name"
        label="Next: Add properties"
        (clicked)="selectOwner(owner)"></sbnb-button>
    </div>
  </div>
</ng-template>

<ng-template #propertiesTemplate>
  <div class="max-w-[640px]">
    <ng-container *ngIf="propertiesState$ | async as propertiesState">
      <h1>Which properties does {{ owner.firstName ? owner.firstName + ' ' + owner.name : owner.name }} own?</h1>
      <ng-container *ngIf="propertiesState.data?.length; else noProperties">
        <p class="mb-8">
          Select one or multiple properties that belong to this Owner. This determines which properties will appear on
          the Owner's Statements.
        </p>

        <section class="border border-smoke rounded p-4 max-h-96 overflow-y-auto">
          <mat-checkbox
            *ngFor="let property of propertiesState.data"
            [disabled]="property?.owners?.length > 0"
            [matTooltip]="property?.owners?.length ? 'This property is already assigned to an owner' : ''"
            class="w-full"
            (change)="toggleProperty($event, property)">
            <div class="flex items-center">
              <img [src]="property.picture" alt="Property image" class="w-7 h-5 rounded mr-2" />
              <span class="flex-1">{{ property.name }}</span>
            </div>
          </mat-checkbox>
        </section>

        <div class="flex justify-end mt-8">
          <sbnb-button
            [loading]="addingProperties"
            [disabled]="!selectedProperties.length"
            label="Next: Create a reservation commission"
            (clicked)="updateOwnerWithProperties(owner)"></sbnb-button>
        </div>
      </ng-container>
      <ng-template #noProperties>
        <div class="mt-8">
          <sbnb-no-items
            icon="properties"
            text="No properties available"
            subtext="Check that your properties are correctly imported and unmuted. Contact support if you think this is an error."></sbnb-no-items>
        </div>
      </ng-template>
    </ng-container>
  </div>
</ng-template>

<ng-template #commissionTemplate>
  <div class="max-w-[640px]">
    <h1 class="mb-8">
      Set up Reservation commission agreements for
      {{ selectedProperties.length }} {{ 'properties' | pluralize: selectedProperties.length }}
    </h1>

    <ng-container *ngIf="commissionState$ | async as commission">
      <ng-container *ngIf="!commission.loading; else loading">
        <ng-container *ngIf="commission.data?.length; else newCommissionTemplate">
          <mat-accordion displayMode="flat" class="example-headers-align" *ngIf="commissionStep$ | async as step">
            <mat-expansion-panel
              [disabled]="step === StepEnum.Existing"
              [expanded]="step === StepEnum.Existing"
              (opened)="setCommissionStep(StepEnum.Existing)"
              hideToggle>
              <mat-expansion-panel-header>
                <mat-panel-title [ngClass]="{ link: step !== StepEnum.Existing }">
                  <div class="flex items-center gap-3">
                    <mat-icon>menu</mat-icon>
                    <h3>Select an existing commission agreement</h3>
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>

              <mat-radio-group
                [(ngModel)]="newCommission"
                (ngModelChange)="newCommission = $event"
                class="flex flex-col">
                <mat-radio-button *ngFor="let commission of commission.data" [value]="commission">
                  <div class="flex items-center">{{ commission.name }}</div>
                </mat-radio-button>
              </mat-radio-group>
            </mat-expansion-panel>

            <mat-expansion-panel
              [disabled]="step === StepEnum.New"
              [expanded]="step === StepEnum.New"
              (opened)="setCommissionStep(StepEnum.New)"
              hideToggle>
              <mat-expansion-panel-header>
                <mat-panel-title [ngClass]="{ link: step !== StepEnum.New }">
                  <div class="flex items-center gap-3">
                    <mat-icon>add_circle</mat-icon>
                    <h3>Create new commission agreement</h3>
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>
              <hospitable-statements-commissions-form
                (agreementChange)="newCommission = $event"
                [showProperties]="false"></hospitable-statements-commissions-form>
            </mat-expansion-panel>
          </mat-accordion>
        </ng-container>
      </ng-container>
    </ng-container>
    <ng-template #newCommissionTemplate>
      <hospitable-statements-commissions-form
        (agreementChange)="newCommission = $event"
        [showProperties]="false"></hospitable-statements-commissions-form>
    </ng-template>

    <div class="flex justify-end mt-6">
      <sbnb-button [loading]="creatingCommission" label="Next: Summary" (clicked)="selectCommission()"></sbnb-button>
    </div>
  </div>
</ng-template>

<ng-template #summaryTemplate>
  <div class="max-w-[640px]">
    <h1 class="mb-4">You're all set! 🎉</h1>
    <p class="mb-2">
      You've successfully added your first owner, assigned properties to them, and set up commission agreements!
    </p>
    <p>
      We'll start to generate statements for you automatically. As they are ready, you can review them and send them to
      Owners.
    </p>

    <div class="flex flex-col md:flex-row gap-4 mt-8">
      <sbnb-button label="View statements" (clicked)="onFinishFlow()"></sbnb-button>
      <!-- <sbnb-button type="secondary" label="Learn about manual transactions"></sbnb-button> -->
    </div>
  </div>
</ng-template>

<ng-template #loading>
  <sbnb-loading-spinner></sbnb-loading-spinner>
</ng-template>
